import socialApiClient from "../helpers/services/social-api-client";

export interface GoogleDriveSetting {
  email?: string;
  name?: string;
  avatarUrl?: string;
  expiresIn?: string;
}

export interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  thumbnailLink?: string;
  size?: string;
  modifiedTime?: string;
  isFolder: boolean;
}

export interface GoogleDriveFilesResponse {
  files: GoogleDriveFile[];
  nextPageToken?: string;
}

export interface GoogleDriveDownloadedFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  data: string;
}

export async function fetchGoogleDriveConnection(
  companyId: string
): Promise<GoogleDriveSetting | null> {
  const { data } = await socialApiClient().get("google/drive", {
    params: { companyId },
  });
  const setting = data?.payload?.googleDriveSetting;
  return setting?.email ? setting : null;
}

export const GOOGLE_DRIVE_OAUTH_MESSAGE_FROM = "Google Drive OAuth";
export const GOOGLE_DRIVE_OAUTH_STORAGE_PREFIX = "google-drive-oauth:";

const APP_DEV_DRIVE_REDIRECT_URI =
  "https://app.dev.wembii.com/social/google/drive";

function usesLocalDriveOAuthCallback(): boolean {
  if (process.env.REACT_APP_GOOGLE_DRIVE_REDIRECT_URI) {
    return true;
  }
  const { hostname, port } = window.location;
  return hostname === "dev.wembii.com" && port === "3000";
}

/** Redirect URI que Google conoce. En local usa el proxy → backend (mismo origen, popup se cierra bien). */
export function getGoogleDriveOAuthRedirectUri(): string {
  if (process.env.REACT_APP_GOOGLE_DRIVE_REDIRECT_URI) {
    return process.env.REACT_APP_GOOGLE_DRIVE_REDIRECT_URI;
  }
  if (usesLocalDriveOAuthCallback()) {
    return `${window.location.origin}/social-api/google/drive/oauth/callback`;
  }
  return APP_DEV_DRIVE_REDIRECT_URI;
}

function createOAuthSessionId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isAllowedGoogleDriveOAuthOrigin(origin: string): boolean {
  if (origin === window.location.origin) return true;
  try {
    const { hostname } = new URL(origin);
    return hostname.endsWith("wembii.com") || hostname === "localhost";
  } catch {
    return false;
  }
}

type OAuthPayload = {
  from?: string;
  code?: string;
  error?: string;
  errorMessage?: string;
};

export async function getGoogleDriveLoginUrl(
  companyId: string,
  oauthSessionId: string
): Promise<string> {
  const redirectUri = getGoogleDriveOAuthRedirectUri();
  const { data } = await socialApiClient().get("google/drive/login", {
    params: {
      companyId,
      redirectUri,
      openerOrigin: window.location.origin,
      oauthSessionId,
    },
  });
  return data?.payload?.url;
}

export async function bindGoogleDrive(
  companyId: string,
  code: string
): Promise<GoogleDriveSetting> {
  const redirectUri = getGoogleDriveOAuthRedirectUri();
  const { data } = await socialApiClient().post(
    "google/drive/bind",
    { code },
    { params: { companyId, redirectUri } }
  );
  return data?.payload?.googleDriveSetting;
}

export async function unbindGoogleDrive(companyId: string): Promise<void> {
  await socialApiClient().post(
    "google/drive/unbind",
    {},
    { params: { companyId } }
  );
}

export async function listGoogleDriveFiles(
  companyId: string,
  params?: {
    folderId?: string;
    pageToken?: string;
    search?: string;
  }
): Promise<GoogleDriveFilesResponse> {
  const { data } = await socialApiClient().get("google/drive/files", {
    params: { companyId, ...params },
  });
  return data?.payload;
}

export async function downloadGoogleDriveFile(
  companyId: string,
  fileId: string
): Promise<GoogleDriveDownloadedFile> {
  const { data } = await socialApiClient().get(
    `google/drive/files/${fileId}/download`,
    { params: { companyId } }
  );
  return data?.payload?.file;
}

export function openGoogleDriveOAuthPopup(
  companyId: string
): Promise<string> {
  const oauthSessionId = createOAuthSessionId();
  const storageKey = `${GOOGLE_DRIVE_OAUTH_STORAGE_PREFIX}${oauthSessionId}`;

  return new Promise(async (resolve, reject) => {
    let settled = false;
    let popup: Window | null = null;
    let pollTimer: number | undefined;
    let closedGraceTimer: number | undefined;
    let handler: ((event: MessageEvent) => void) | undefined;
    let closedCheckScheduled = false;

    sessionStorage.removeItem(storageKey);

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      if (handler) {
        window.removeEventListener("message", handler);
      }
      if (pollTimer !== undefined) {
        window.clearInterval(pollTimer);
      }
      if (closedGraceTimer !== undefined) {
        window.clearTimeout(closedGraceTimer);
      }
      sessionStorage.removeItem(storageKey);
      try {
        popup?.close();
      } catch {
        // popup may already be closed
      }
      fn();
    };

    const readStoredPayload = (): OAuthPayload | null => {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      try {
        return JSON.parse(raw) as OAuthPayload;
      } catch {
        return null;
      }
    };

    const handlePayload = (payload: OAuthPayload | null): boolean => {
      if (!payload || payload.from !== GOOGLE_DRIVE_OAUTH_MESSAGE_FROM) {
        return false;
      }

      if (payload.error) {
        finish(() =>
          reject(new Error(payload.errorMessage || "OAuth failed"))
        );
        return true;
      }

      if (payload.code) {
        finish(() => resolve(payload.code as string));
        return true;
      }

      return false;
    };

    const scheduleClosedGraceCheck = () => {
      if (closedCheckScheduled) return;
      closedCheckScheduled = true;

      closedGraceTimer = window.setTimeout(() => {
        if (handlePayload(readStoredPayload())) return;
        finish(() => reject(new Error("OAuth cancelled")));
      }, 900);
    };

    try {
      const url = await getGoogleDriveLoginUrl(companyId, oauthSessionId);
      if (!url) {
        reject(new Error("OAuth URL missing"));
        return;
      }

      handler = (event: MessageEvent) => {
        if (!isAllowedGoogleDriveOAuthOrigin(event.origin)) return;
        handlePayload(event.data as OAuthPayload);
      };

      window.addEventListener("message", handler);
      popup = window.open(
        url,
        "google-drive-oauth",
        "width=520,height=680,menubar=no,toolbar=no"
      );

      if (!popup) {
        finish(() => reject(new Error("Popup blocked")));
        return;
      }

      pollTimer = window.setInterval(() => {
        if (handlePayload(readStoredPayload())) return;

        if (popup?.closed) {
          scheduleClosedGraceCheck();
        }
      }, 250);
    } catch (error) {
      finish(() => reject(error as Error));
    }
  });
}
